import { useContext, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';

import { hasComplexity, hasMinLength, Input, isRequired, Submit } from 'components/Form';
import { Link } from 'components/Link';
import { privateSsoProviders, SSOProvidersKeys } from 'components/SSOButtons';
import { Text } from 'components/Text';
import { RedirectContext } from 'context/Redirect';
import { TrackingContext } from 'context/Tracking';
import { SSOProvider, User, UserContext } from 'context/User';
import { useMfaPromptDismissal } from 'hooks/useMfaPromptDismissal';
import { forgotPasswordRoutes } from 'pages/ForgotPassword/routes';
import { mfaLoginRoutes } from 'pages/MfaLogin/routes';
import { mfaSetupRoutes } from 'pages/MfaSetup/routes';
import { signUpRoutes } from 'pages/SignUp/routes';
import { getSourcesAsQueryParams, getSSItem } from 'tracking';
import { Endpoints, get, MfaStatusResponse, post } from 'utils/api';
import { getTermsUserNeedsToAccept } from 'utils/legalTerms';
import { useHistory, useLocationState } from 'utils/router';

import { SignInErrorCodes } from './errorCodes';
import { signInRoutes } from './routes';
import { SignInFormErrorBox } from './SignInFormErrorBox';

interface SignInFormValues {
  login: string;
  password: string;
}

export interface SignInFormProps {
  onSubmit: () => void;
  onError: () => void;
  onSuccess: () => void;
  onShowMfaPrompt?: (redirect: () => void) => void;
  onShowTermsModal?: (onAccept: () => Promise<void>, pendingTerms: string[]) => void;
  forceTerms?: boolean;
}

const formName = 'sign-in';

export const SignInForm = ({
  onSubmit,
  onError,
  onSuccess,
  onShowMfaPrompt,
  onShowTermsModal,
  forceTerms = false,
}: SignInFormProps) => {
  const state = useLocationState<{ errorCode?: SignInErrorCodes }>();
  const [, { getUser, setUser }] = useContext(UserContext);
  const [
    { instance, to, customContent },
    { redirectToPortal, redirectToStateInstanceHome, redirectToTo, redirectToCustomLoginRedirect },
  ] = useContext(RedirectContext);
  const [showPasswordField, setShowPasswordField] = useState(false);
  const [, { faro }] = useContext(TrackingContext);
  const [serverError, setServerError] = useState<string | null>(null);
  const history = useHistory();
  const [submitLabel, setSubmitLabel] = useState<string>('Sign In');
  const [isUsernameInputDisabled, setUsernameInputDisabled] = useState(false);
  const { isDismissed } = useMfaPromptDismissal();

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<SignInFormValues>({
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const checkMfaAndShowPrompt = async (redirect: () => void) => {
    try {
      const mfaStatus = await get<MfaStatusResponse>(Endpoints.MFA_STATUS);

      if (!mfaStatus.available) {
        faro.trackEvent('mfa_prompt_suppressed', {
          reason: 'not_available',
        });
        redirect();
        return;
      }

      if (!mfaStatus.enabled) {
        const dismissed = isDismissed();

        if (dismissed) {
          faro.trackEvent('mfa_prompt_suppressed', {
            reason: 'recently_dismissed',
          });
          redirect();
        } else if (onShowMfaPrompt) {
          faro.trackEvent('mfa_prompt_shown', {
            trigger: 'sign_in',
          });
          onShowMfaPrompt(redirect);
        } else {
          redirect();
        }
      } else {
        redirect();
      }
    } catch (err) {
      faro.trackError(err);
      redirect();
    }
  };

  const handleFormSubmit: SubmitHandler<SignInFormValues> = async (values) => {
    setServerError(null);
    history.replace(signInRoutes.base);

    onSubmit();

    if (privateSsoProviders.length > 0) {
      const queryParams = new URLSearchParams(getSourcesAsQueryParams());
      if (!queryParams.get('signupForm')) {
        queryParams.set('signupForm', getSSItem('signupForm') || 'cloud_trial');
      }

      const provider = privateSsoProviders[0];
      window.location.href = `/api/login/${provider.key}?${queryParams.toString()}`;
    } else if (!values.password) {
      try {
        faro.trackFormSubmit(formName);
        const ssoProvidersData = (await post<SSOProvider[]>(Endpoints.CHECK_USER_SSO, {
          username: values.login,
        })) as SSOProvider[];

        faro.trackFormSubmitSuccess(formName);
        setUsernameInputDisabled(true);
        onSuccess();

        if (!ssoProvidersData || ssoProvidersData.length === 0) {
          history.push(signInRoutes.base);
          setShowPasswordField(true);
          setUsernameInputDisabled(false);
        } else {
          for (const provider of ssoProvidersData) {
            let key: SSOProvidersKeys = provider.slug as SSOProvidersKeys;
            privateSsoProviders.push({
              key: key,
              name: provider.name,
              provider: provider.provider,
            });
            setSubmitLabel('Log in with ' + provider.name);
          }
          history.push(signInRoutes.base);
        }
      } catch {
        setSubmitLabel('Log in with email');
        history.push(signInRoutes.base);
        setShowPasswordField(true);
        setUsernameInputDisabled(false);
      }
    } else {
      try {
        await post<User>(Endpoints.SIGN_IN, values);

        const newUserData = (await getUser()) as User;
        await setUser(newUserData);

        faro.trackFormSubmitSuccess(formName);

        onSuccess();

        if (!newUserData.emailConfirmed) {
          history.push(signUpRoutes.checkInbox);
        } else {
          let redirect: () => void;
          const hasOrgs = newUserData.memberships?.length > 0;
          if (customContent?.loginRedirect && (hasOrgs || customContent.skipOrgCreation)) {
            redirect = redirectToCustomLoginRedirect;
          } else if (to && !customContent) {
            redirect = redirectToTo;
          } else if (instance) {
            redirect = redirectToStateInstanceHome;
          } else {
            redirect = () => redirectToPortal(newUserData);
          }

          try {
            const result = await getTermsUserNeedsToAccept(forceTerms);
            if (!result) {
              await checkMfaAndShowPrompt(redirect);
              return;
            }
            const { latestTerms, termsToAccept } = result;
            if (termsToAccept.length > 0 && onShowTermsModal) {
              onShowTermsModal(async () => {
                try {
                  await post(Endpoints.ACCEPT_LEGAL_TERMS, { legalTermsVersion: latestTerms });
                } catch (err) {
                  faro.trackError(err);
                }
                await checkMfaAndShowPrompt(redirect);
              }, termsToAccept);
            } else {
              await checkMfaAndShowPrompt(redirect);
            }
          } catch (err) {
            faro.trackError(err);
            await checkMfaAndShowPrompt(redirect);
          }
        }
      } catch (err) {
        if (err.message === 'MFA verification required') {
          history.push(mfaLoginRoutes.base);
          return;
        }

        if (err.message === 'MFA setup required') {
          history.push(mfaSetupRoutes.base, { setupRequired: true });
          return;
        }

        faro.trackFormSubmitError(formName, err.message);
        faro.trackError(err);
        setServerError(err.message);

        onError();
      }
    }
  };

  return (
    <form id="form" noValidate onSubmit={handleSubmit(handleFormSubmit)}>
      <Input
        control={control}
        disabled={isSubmitting || isUsernameInputDisabled}
        errors={errors}
        id="username"
        label="Email or username"
        name="login"
        type="text"
        validate={[isRequired()]}
      />
      {showPasswordField && (
        <Input
          control={control}
          disabled={isSubmitting}
          errors={errors}
          id="password"
          label="Password"
          name="password"
          type="password"
          validate={[isRequired(), hasMinLength(6), hasComplexity()]}
        />
      )}
      {showPasswordField && (
        <Text align="right" id="forgot-password-link-container" type="small">
          <Link id="forgot-password-link" label="Forgot password?" to={forgotPasswordRoutes.recover} />
        </Text>
      )}
      {isUsernameInputDisabled && (
        <Text align="right" id="cancel-link-container" type="small">
          <Link
            id="cancel-link"
            label="Cancel"
            onClick={() => {
              setUsernameInputDisabled(false);
              privateSsoProviders.length = 0;
              setSubmitLabel('Sign in');
            }}
            to={signInRoutes.base}
          />
        </Text>
      )}
      <Submit
        disabled={isSubmitting}
        id="submit"
        isLoading={isSubmitting}
        label={submitLabel}
        loadingText="Logging in..."
      />
      <SignInFormErrorBox code={state?.errorCode ?? null} text={serverError} />
    </form>
  );
};
